import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { vehicleService } from '../../application/VehicleService';
import { ProjectActionType } from '../../context/project/actions';
import { useProjectContext } from '../../context/project/context';
import { Project } from '../../domain/project/Project';
import { VehicleFlat } from '../../domain/vehicle/VehicleFlat';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';

import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseRetrieveFlatVehiclesProps {
  project?: Project;
}

type UseRetrieveFlatVehiclesResponse = ServiceResponse<Function, Array<VehicleFlat>>;

// Hooks act as 'Adapter' layer.
export const useRetrieveFlatVehicles = ({ project }: UseRetrieveFlatVehiclesProps): UseRetrieveFlatVehiclesResponse => {
  const { projectState, projectDispatch } = useProjectContext();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const {
    isInitialLoading: isLoading,
    refetch,
    isError,
    isSuccess,
  } = useQuery(
    [`planner_flat_vehicles`, project?.id],
    (): Promise<Array<VehicleFlat>> => {
      return vehicleService.fetchFlatVehicles({
        project: project!,
      });
    },
    {
      enabled: !!project,
      retry: false,
      staleTime: Infinity,
      onSuccess: (flatVehicles) => {
        projectDispatch({
          type: ProjectActionType.SEARCH_FLAT_VEHICLES_SUCCESS,
          payload: {
            flatVehicles,
          },
        });
      },
      onError: (err: any) => {
        console.info(`Planner Retrieve Flat Vehicles Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading,
    success: isSuccess,
    run: refetch,
    data: projectState.vehicles.flat ?? [],
  };
};
